import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, DataBlock, Grid } from '@fs/zion-ui'
import { PersonLink, RelationshipLink, TreePersonLink, UserLink } from '../../../app/Links'
import { isNullOrUndefined } from '../../../../util/Utils'
import { LabelsBlock } from '../../commonComponents/CommonComponents'
import { TreeIdCells } from './TreeIdBlock'

export default function InappropriateMessageSummary({ messageDetails, term, queueCachesState, queueCachesActions }) {
  const { t } = useTranslation()
  const optionalPersonId = messageDetails?.personId || null
  const contributorId = messageDetails?.contributorId

  let personSummary = null
  let personLabels = null
  const userSummary = queueCachesState?.usersCache[contributorId]

  if (optionalPersonId) {
    personSummary = queueCachesState?.personsCache[optionalPersonId]
    personLabels = queueCachesState.labelsCache[optionalPersonId]
  }

  useEffect(() => {
    if (optionalPersonId) {
      queueCachesActions.fetchPersonSummary(optionalPersonId)
      queueCachesActions.fetchPersonLabels(optionalPersonId)
    }
    queueCachesActions.fetchUserSummary(contributorId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // eslint-disable-next-line prettier/prettier
  if (optionalPersonId && (isNullOrUndefined(personSummary) || isNullOrUndefined(personLabels)) || isNullOrUndefined(userSummary)) {
    return null
  }

  return (
    <Grid>
      {/* Row personId or coupleId or parentChildId */}
      <Cell columns={6}>
        {optionalPersonId && (
          <DataBlock
            heading={t('person.label', 'Person')}
            data={
              <>
                <PersonLink personId={optionalPersonId} personName={personSummary?.summary?.name} showId newTab />
                <TreePersonLink personId={optionalPersonId} treeIcon newTab />
              </>
            }
          />
        )}
        {messageDetails.coupleId && (
          <DataBlock
            heading={t('queues.coupleId', 'Couple Id')}
            data={<RelationshipLink relationshipType="couple" relationshipId={messageDetails.coupleId} newTab />}
          />
        )}
        {messageDetails.associationId && (
          <DataBlock
            heading={t('queues.associationId', 'Association Id')}
            data={
              <RelationshipLink relationshipType="association" relationshipId={messageDetails.associationId} newTab />
            }
          />
        )}
        {messageDetails.parent_childId && (
          <DataBlock
            heading={t('queues.parentchildId', 'ParentChild Id')}
            data={
              <RelationshipLink relationshipType="parentchild" relationshipId={messageDetails.parent_childId} newTab />
            }
          />
        )}
      </Cell>
      <Cell columns={6}>
        <DataBlock
          heading={t('contributor', 'Contributor')}
          data={<UserLink userId={contributorId} userName={userSummary?.contactName} showId newTab />}
        />
      </Cell>

      <TreeIdCells treeId={messageDetails?.treeId} isResearcherTree={messageDetails?.researcherTree} />

      {/* Row */}
      {messageDetails?.[term] && (
        <Cell columns={12}>
          <DataBlock heading={t(`queues.${term}`, term)} data={messageDetails?.[term]} />
        </Cell>
      )}
      {messageDetails?.term && (
        <Cell columns={12}>
          <DataBlock heading={t('queues.term', 'Term')} data={messageDetails?.term} />
        </Cell>
      )}

      {/* Row */}
      {messageDetails?.unwantedText && (
        <Cell columns={6}>
          <DataBlock
            heading={t('queues.unwantedText', 'Unwanted Text')}
            data={
              <HighlightTermInText
                text={messageDetails?.unwantedText}
                term={messageDetails?.unwantedTerm || messageDetails?.term}
              />
            }
          />
        </Cell>
      )}
      {messageDetails?.change && (
        <Cell columns={6}>
          <DataBlock heading={t('queues.changeLocation', 'Change Location')} data={messageDetails?.change} />
        </Cell>
      )}
      {messageDetails?.birthDate && (
        <Cell columns={6}>
          <DataBlock heading={t('queues.birthDate', 'Birth Date')} data={messageDetails?.birthDate} />
        </Cell>
      )}
      {messageDetails?.birthPlace && (
        <Cell columns={6}>
          <DataBlock heading={t('queues.birthPlace', 'Birth Place')} data={messageDetails?.birthPlace} />
        </Cell>
      )}
      {messageDetails?.deathDate && (
        <Cell columns={6}>
          <DataBlock heading={t('queues.deathDate', 'Death Date')} data={messageDetails?.deathDate} />
        </Cell>
      )}
      {messageDetails?.deathPlace && (
        <Cell columns={6}>
          <DataBlock heading={t('queues.deathPlace', 'Death Place')} data={messageDetails?.deathPlace} />
        </Cell>
      )}

      {/* Row */}
      <LabelsBlock personLabels={personLabels} />
    </Grid>
  )
}

function HighlightTermInText({ text, term }) {
  const parts = text.split(new RegExp(`(${term})`, 'gi'))
  return (
    <span>
      {parts.map((part) => (
        <span key={part} style={part.toLowerCase() === term?.toLowerCase() ? { backgroundColor: 'yellow' } : {}}>
          {part}
        </span>
      ))}
    </span>
  )
}
